/* eslint-disable @typescript-eslint/no-explicit-any */
import { GenericType } from "@/types";

export function updateObject(obj: GenericType, path: string, value: string[]) {
  const keys = path.split(".");
  const newObj = structuredClone(obj); // Deep clone the object

  let current = newObj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (current?.[keys?.[i]]) {
      current = current[keys[i]];
    }
  }

  current[keys[keys.length - 1]] = value;

  return newObj;
}

function deleteObject(obj: GenericType, path: string): GenericType {
  const keys = path.split(".");
  const newObj = structuredClone(obj); // Deep clone the object

  let current = newObj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!(keys[i] in current)) {
      return newObj; // Return original object if the path doesn't exist
    }
    current = current[keys[i]];
  }

  const lastKey = keys[keys.length - 1];

  if (Array.isArray(current)) {
    const index = parseInt(lastKey, 10);
    if (!isNaN(index) && index >= 0 && index < current.length) {
      current.splice(index, 1); // Remove the item from the array
    }
  } else {
    delete current[lastKey]; // Delete the property for objects
  }

  return newObj;
}

export function addSibling(
  obj: GenericType,
  path: string,
  newData: GenericType,
) {
  const keys = path.split(".");
  const newObj = structuredClone(obj); // Deep clone the object

  let current = newObj;
  for (let i = 0; i < keys.length - 1; i++) {
    current = current[keys[i]];
  }

  const lastKey = keys[keys.length - 1];
  if (Array.isArray(current[lastKey])) {
    current[lastKey].push(newData); // Add new data as a sibling
  } else {
    throw new Error("Target is not an array, cannot add a sibling");
  }

  return newObj;
}

export function getObject(obj: GenericType, path: string): GenericType {
  const keys = path.split(".");
  let current: GenericType = obj;

  for (const key of keys) {
    if (current == null || !(key in current)) {
      throw new Error(`Invalid path: ${path}`);
    }
    current = current[key];
  }

  return current;
}

export const objectMutation = {
  update: updateObject,
  delete: deleteObject,
  get: getObject,
  addArraySibling: addSibling,
};

export function isKeyExist(
  obj: Record<string, GenericType>,
  key: string,
): boolean {
  if (typeof obj !== "object" || obj === null) {
    return false;
  }

  // Use Object.prototype.hasOwnProperty safely
  if (Object.prototype.hasOwnProperty.call(obj, key)) {
    return true;
  }

  for (const k in obj) {
    if (typeof obj[k] === "object" && isKeyExist(obj[k], key)) {
      return true;
    }
  }

  return false;
}

export function findValueByKey<T = any>(
  data: unknown,
  targetKey: string,
): T | undefined {
  if (data === null || data === undefined) return undefined;

  if (Array.isArray(data)) {
    for (const item of data) {
      const result = findValueByKey<T>(item, targetKey);
      if (result !== undefined) {
        return result;
      }
    }
    return undefined;
  }

  if (typeof data === "object") {
    const obj = data as Record<string, unknown>;

    if (targetKey in obj) {
      return obj[targetKey] as T;
    }

    for (const value of Object.values(obj)) {
      const result = findValueByKey<T>(value, targetKey);
      if (result !== undefined) {
        return result;
      }
    }
  }

  return undefined;
}
